import { useSyncExternalStore } from 'react';
import { authModule } from '@/auth/auth-module';
import { getUserProfile, subscribeUserProfile } from '@/auth/user-profile';
import { getUserInitials } from './user-initials';

export interface UserDisplay {
  /** Human display name — real name when known, else email. Never blank when signed in. */
  name: string;
  email: string;
  /** 1-2 letter avatar initials (smart email fallback: `john.doe@…` → `JD`). */
  initials: string;
  /**
   * `userName` value to feed `MotifAvatar` so its naive initials splitter
   * (first char of first + last space-separated token) reproduces `initials`
   * exactly — e.g. `"JD"` → `"J D"` → renders `"JD"`.
   */
  avatarSeed: string;
}

/**
 * Resolve the signed-in user's display name + avatar initials.
 *
 * Prefers the RUM user-details profile (correct `firstName`/`lastName` for
 * external / guest users) over the MSAL account, whose `name` is empty for
 * guests and falls back to the raw email. Mirrors legacy
 * `AuthorizeService.mapUserDetails` + `HeaderService.getInitials`.
 */
export function useUserDisplay(): UserDisplay {
  const profile = useSyncExternalStore(subscribeUserProfile, getUserProfile, getUserProfile);
  const account = authModule.user;

  const email = profile?.email || account?.email || '';
  const realName = profile ? `${profile.firstName} ${profile.lastName}`.trim() || profile.name : '';
  const displayName = realName || account?.name || '';
  const name = displayName || email;

  // Compute initials from the real name when we have one; otherwise fall back
  // to the email local-part split (`getUserInitials` handles that).
  const initials = getUserInitials({ name: displayName, email });
  const avatarSeed = initials.split('').join(' ');

  return { name, email, initials, avatarSeed };
}
